import { useMemo } from 'react';
import { getTopicInstructionalRollup } from '../../utils/topicInstructionalUnits';
import {
    LessonStateKey,
    UnitStateKey
} from '../../../InProgressUnitsPanel/lessonStepState';

export interface UseTopicStateOptions {
  units?: any[];
  /** Unused by the hook; accepted for call-site convenience. */
  passFailThreshold?: number | null;
  studentCurriculumData?: any;
  isStudentCurriculum: boolean;
}

export function useTopicState({ units, studentCurriculumData, isStudentCurriculum }: UseTopicStateOptions) {
  const hasUnitsFacet = !!(units && units.length > 0);

  const getTopicStateFromStudentCurriculum = useMemo(() => {
    return (topicId: string): any => {
      if (!studentCurriculumData?.e180_Subjects) return null;

      for (const subject of studentCurriculumData.e180_Subjects) {
        for (const keyStage of subject.e180_KeyStages || []) {
          for (const year of keyStage.e180_Years || []) {
            for (const topic of year.e180_Topics || []) {
              if (topic.e180_TopicId === topicId) {
                return topic.e180_State;
              }
              const checkChildTopics = (topics: any[]): any => {
                for (const childTopic of topics || []) {
                  if (childTopic.e180_TopicId === topicId) {
                    return childTopic.e180_State;
                  }
                  if (childTopic.e180_ChildTopics && childTopic.e180_ChildTopics.length > 0) {
                    const result = checkChildTopics(childTopic.e180_ChildTopics);
                    if (result) return result;
                  }
                }
                return null;
              };
              if (topic.e180_ChildTopics && topic.e180_ChildTopics.length > 0) {
                const result = checkChildTopics(topic.e180_ChildTopics);
                if (result) return result;
              }
            }
          }
        }
      }
      return null;
    };
  }, [studentCurriculumData]);

  const getAssessmentScoreForTopic = useMemo(() => {
    return (topicId: string): number | null => {
      if (!units || units.length === 0 || !topicId) return null;

      const normalizeId = (id: string) => id?.toLowerCase().trim();
      const normalizedTopicId = normalizeId(topicId);

      const matchingUnits = units.filter(unit => {
        const isCompleted = unit.e251_State === UnitStateKey.Complete;
        const hasAssessment = unit.e251_AssessmentId && unit.e251_AssessmentStatus?.e251_Result?.e251_Score != null;
        const containsTopic = unit.e251_TopicIds?.some((id: string) => {
          if (!id) return false;
          return normalizeId(id) === normalizedTopicId;
        });
        return isCompleted && hasAssessment && containsTopic;
      });

      if (matchingUnits.length === 0) return null;

      const mostRecentUnit = matchingUnits[0];
      return mostRecentUnit.e251_AssessmentStatus?.e251_Result?.e251_Score ?? null;
    };
  }, [units]);

  const isTopicCompleted = useMemo(() => {
    return (topic: any): boolean => {
      const topicId = topic.e180_TopicId || topic.e179_TopicId;
      if (hasUnitsFacet && topicId) {
        const { rollup, qualifyingUnits } = getTopicInstructionalRollup(units, topicId);
        if (qualifyingUnits.length > 0) {
          return rollup === 'allComplete';
        }
      }

      let state = topic.e180_State || topic.e179_State;
      if (isStudentCurriculum && !state && topic.e179_TopicId) {
        state = getTopicStateFromStudentCurriculum(topic.e179_TopicId);
      }
      if (!state) return false;
      const keys = state.selectedKeys || state.Values || [];
      return keys.includes(LessonStateKey.Completed);
    };
  }, [isStudentCurriculum, getTopicStateFromStudentCurriculum, hasUnitsFacet, units]);

  const isTopicInProgress = useMemo(() => {
    return (topic: any): boolean => {
      const topicId = topic.e180_TopicId || topic.e179_TopicId;
      if (hasUnitsFacet && topicId) {
        const { rollup, qualifyingUnits } = getTopicInstructionalRollup(units, topicId);
        if (qualifyingUnits.length > 0) {
          return rollup === 'active';
        }
      }

      let state = topic.e180_State || topic.e179_State;
      if (isStudentCurriculum && !state) {
        const tid = topic.e179_TopicId || topic.e180_TopicId;
        if (tid) {
          state = getTopicStateFromStudentCurriculum(tid);
        }
      }
      if (!state) return false;
      const keys = state.selectedKeys || state.Values || [];
      return keys.includes(LessonStateKey.InProgress);
    };
  }, [isStudentCurriculum, getTopicStateFromStudentCurriculum, hasUnitsFacet, units]);

  const isTopicReady = useMemo(() => {
    return (topic: any): boolean => {
      const topicId = topic.e180_TopicId || topic.e179_TopicId;
      if (hasUnitsFacet && topicId) {
        const { rollup, qualifyingUnits } = getTopicInstructionalRollup(units, topicId);
        if (qualifyingUnits.length > 0) {
          return false;
        }
      }

      let state = topic.e180_State || topic.e179_State;
      if (isStudentCurriculum && !state) {
        const tid = topic.e179_TopicId || topic.e180_TopicId;
        if (tid) {
          state = getTopicStateFromStudentCurriculum(tid);
        }
      }
      if (!state) return true;
      const keys = state.selectedKeys || state.Values || [];
      const hasReady = keys.includes(LessonStateKey.Ready);
      const hasInProgress = keys.includes(LessonStateKey.InProgress);
      const hasCompleted = keys.includes(LessonStateKey.Completed);
      return hasReady && !hasInProgress && !hasCompleted;
    };
  }, [isStudentCurriculum, getTopicStateFromStudentCurriculum, hasUnitsFacet, units]);

  return {
    isTopicCompleted,
    isTopicInProgress,
    isTopicReady,
    getAssessmentScoreForTopic,
    getTopicStateFromStudentCurriculum
  };
}
